import { AsgardDocPdf } from '../../core/documents/asgard-doc-pdf';
import { AsgardDocBase } from '../../core/documents/asgard-doc-base';
import { Injectable } from '@angular/core';
import { DocumentManagerService } from './document-manager.service';
import { isAnnotationType } from 'src/app/core/common';
import { AnnotationTypes } from 'src/app/core/pdf-utils/enums';
import { PdfInstanceManagerService } from '../instance/pdf-instance-manager.service';
import { AnnotationEvent } from 'src/models/Events';
import { ExamModeRestrictionsService } from '../exam/exam-mode-restrictions.service';
import { ReportExamService } from '../exam/report-exam.service';
import { AnnotationAction, ReportLogLevel } from '../exam/enums/report-level.enum';
import { Core } from '@pdftron/webviewer';


@Injectable({
  providedIn: 'root'
})
export class DocumentAnnotationService {
  private lastHighlitedWordIdx: number | null = null;

  private get currentDoc(): AsgardDocBase | null | undefined { return this.docMgrService.currentDoc; }

  constructor(
    private readonly docMgrService: DocumentManagerService,
    private readonly pdfInstanceManager: PdfInstanceManagerService,
    private readonly examService: ExamModeRestrictionsService,
    private readonly reportExamService: ReportExamService,
  ) {
    this.initSubscriptions();
  }

  getEmptyTextNotes(): Core.Annotations.Annotation[] {
    if (this.currentDoc instanceof AsgardDocPdf && this.currentDoc.annotationManager) {
      const annotations = this.currentDoc.annotationManager.getAnnotationsList();
      const textNotes = annotations.filter(x => isAnnotationType(x, [AnnotationTypes.FreeText, AnnotationTypes.StickyNote]));
      const emptyNotes = textNotes.filter(x => !!!x.getContents());
      return emptyNotes;
    }

    return [];
  }
  
  deleteAnnotation(annotation: Core.Annotations.Annotation, force = false) {
    if (this.currentDoc instanceof AsgardDocPdf && this.currentDoc.annotationManager) {
      this.currentDoc.annotationManager.deleteAnnotation(annotation, { imported: false, force });
    }
  }

  deleteAnnotations(annotations: Core.Annotations.Annotation[], force = false) {
    if (this.currentDoc instanceof AsgardDocPdf && this.currentDoc.annotationManager) {
      this.currentDoc.annotationManager.deleteAnnotations(annotations, { imported: false, force });
    }
  }

  private annotationsAddedHandler = (annotations: Core.Annotations.Annotation[]) => {
    if (this.currentDoc instanceof AsgardDocPdf) {
      const textNotes = annotations.filter(x => isAnnotationType(x, [AnnotationTypes.FreeText]));
      this.currentDoc.addAnnotations(textNotes);
    }
  }

  private annotationsDeletedHandler = (annotations: Core.Annotations.Annotation[]) => {
    if (this.currentDoc instanceof AsgardDocPdf) {
      const textNotes = annotations.filter(x => isAnnotationType(x, [AnnotationTypes.FreeText, AnnotationTypes.StickyNote]));
      this.currentDoc.deleteAnnotations(textNotes.map(x => x.Id));
    }
  }

  private annotationsUpdatedHandler = (annotations: Core.Annotations.Annotation[]) => {
    if (this.currentDoc instanceof AsgardDocPdf) {
      const textNotes = annotations.filter(x => isAnnotationType(x, [AnnotationTypes.FreeText]));
      this.currentDoc.updateAnnotations(textNotes);
    }
  }

  private annotationHoveredHandler = async (annotation: Core.Annotations.Annotation) => {
    if (!this.currentDoc || this.docMgrService.isFocusDocument || !this.currentDoc.isDocumentReady
      || (annotation && !isAnnotationType(annotation, [AnnotationTypes.FreeText]))) {

      return;
    }

    const wordIdx = await (this.currentDoc as AsgardDocPdf).highlightWordBeforeTextNote(annotation, this.lastHighlitedWordIdx);

    if (wordIdx || wordIdx === 0) {
      this.lastHighlitedWordIdx = wordIdx;
    }
  }

  private initSubscriptions() {
    this.pdfInstanceManager.annotationAdded.subscribe(
      (e: AnnotationEvent<Core.Annotations.Annotation>) => {
        if (e && e.annotations && e.annotations.length) {
          this.logAnnotationActionIfNeeded(AnnotationAction.Added, e.annotations);
          this.annotationsAddedHandler(e.annotations);
        }
      }
    );

    this.pdfInstanceManager.annotationDeleted.subscribe(
      (e: AnnotationEvent<Core.Annotations.Annotation>) => {
        if (e && e.annotations && e.annotations.length) {
          this.logAnnotationActionIfNeeded(AnnotationAction.Deleted, e.annotations);
          this.annotationsDeletedHandler(e.annotations);
        }
      }
    );

    this.pdfInstanceManager.annotationModified.subscribe(
      (e: AnnotationEvent<Core.Annotations.Annotation>) => {
        if (e && e.annotations && e.annotations.length) {
          this.logAnnotationActionIfNeeded(AnnotationAction.Modified, e.annotations);
          this.annotationsUpdatedHandler(e.annotations);
        }
      }
    );

    this.pdfInstanceManager.annotationHovered.subscribe(
      (e: AnnotationEvent<Core.Annotations.Annotation>) => {
        if (e && e.annotations && e.annotations.length) {
          this.annotationHoveredHandler(e.annotations[0]);
        }
      }
    );
  }

  private async logAnnotationActionIfNeeded(action: AnnotationAction, annotations: Core.Annotations.Annotation[]) {
    if (!this.examService.isExamInProgress) {
      return;
    }

    if (!annotations.some(annot => isAnnotationType(annot, [
      AnnotationTypes.FreeText, AnnotationTypes.StickyNote, AnnotationTypes.Rectangle
    ]))) {
      return;
    }

    const logs = annotations.map(a => this.mapAnnotationToLog(action, a));
    const promises = logs.map(
      l => new Promise(() => 
        this.reportExamService.sendCustomMessageReport(ReportLogLevel.TextNotes, JSON.stringify(l)))
    );

    await Promise.all(promises);
  }

  private mapAnnotationToLog(action: AnnotationAction, annot: Core.Annotations.Annotation): AnnotationActionData {
    return {
      action,
      id: annot.Id,
      pageNumber: annot.getPageNumber(),
      content: annot.getContents(),
      subject: annot.Subject,
    };
  }
}

type AnnotationActionData = {
  action: AnnotationAction;
  id: string;
  pageNumber: number;
  content: string;
  subject: string;
}
